import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, MailCheck } from 'lucide-react';
import TurnstileWidget from '@/components/auth/TurnstileWidget';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email, turnstile_token: turnstileToken || undefined });
    } finally {
      // Same generic outcome regardless of success/failure — no email enumeration.
      setSent(true);
      setSubmitting(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#F72585]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <MailCheck className="w-7 h-7 text-[#F72585]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Recupera tu contraseña</h1>
          <p className="text-sm text-[#64748B] mt-1">Te enviaremos un enlace para restablecerla</p>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <p className="text-sm text-[#0F172A]">
                Si el correo existe, se enviará un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full mt-6 bg-[#0F172A] text-white hover:bg-[#1E293B]">
                Volver a iniciar sesión
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-password-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="pl-10 h-11 bg-white border-[#E2E8F0] rounded-lg text-sm focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <TurnstileWidget ref={turnstileRef} action="forgot_password" onToken={setTurnstileToken} />
              </div>

              <Button
                type="submit"
                data-testid="forgot-password-submit-button"
                disabled={submitting}
                className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] rounded-lg font-medium text-sm transition-all"
              >
                {submitting ? 'Enviando...' : 'Enviar enlace'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Volver a iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
